import React, { useEffect, useState } from 'react';
import { FC } from 'react';
import { useSelector } from 'react-redux';

import useTranslations from '@hooks/useTranslation';
import moment from 'moment';
import 'moment/locale/sk';
import { COMPONENT_TYPE, IColors, IConferenceItem, IEventDate } from '../../interfaces/Conference';
import { RootState } from '../../store';
import {
    Title,
    TitleSymbol,
    Wrapper,
} from './ConferenceStream.elements';
import VideoJS from '@ui/VideoJS/videojs';
import 'video.js/dist/video-js.css';
import Cookies from 'js-cookie';
import { ACCESS_TOKEN_KEY_GUEST } from '../../constants';

interface ConferenceProps {
    colors?: IColors;
    conf?: IConferenceItem;
}

const ConferenceHeaderTemplate: FC<ConferenceProps> = ({ colors, conf }) => {
    const { t } = useTranslations();

    const isLoggedInState = useSelector((state: RootState) => state.ui.isLoggedIn);

    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const [termAccessible, setTermAccessible] = useState(false);

    const [startConf, setStartConf] = useState('');
    const [endConf, setEndConf] = useState('');
    const [validUrl, setValidUrl] = useState(false);
    const [isStreamingDay, setIsStreamingDay] = useState(false);
    const [todayStart, setTodayStart] = useState('');
    const [todayEnd, setTodayEnd] = useState('');

    const [nextDayStart, setNextDayStart] = useState('');

    const [times, setTimes] = useState([]);

    const playerRef = React.useRef(null);

    const [streamOptions, setStreamOptions] = useState({});

    const handlePlayerReady = (player: any) => {
        playerRef.current = player;
    };

    useEffect(() => {
        setIsLoggedIn(isLoggedInState);
    }, [isLoggedInState]);

    useEffect(() => {
        if (conf) {
            for (const item of conf?.dates) {
                if (item.accessible && item.date.conference_stream_days) {
                    setTimes(item.date.conference_stream_days);
                    setTermAccessible(true);
                    break;
                }
            }
        }
    }, [conf]);

    useEffect(() => {
        if (times.length > 0) {

            const startTimeDays = times.map((item: any) => item.from);
            const startTimes = startTimeDays.reduce(function (a, b) {
                return a < b ? a : b;
            });
            setStartConf(startTimes);

            const endTimeDays = times.map((item: any) => item.to);
            const endTimes = endTimeDays.reduce(function (a, b) {
                return a > b ? a : b;
            });
            setEndConf(endTimes);

            const todayStream = times.filter((item) => moment(item?.from).format('D.M.YYYY') == moment().format('D.M.YYYY'));
            if (todayStream.length === 1) {
                setIsStreamingDay(true);
                setTodayStart(todayStream[0].from);
                setTodayEnd(todayStream[0].to);
                handleCheckUrl(`${todayStream[0].url}.m3u8`)
            }

            const nextDay = moment().subtract(-1, 'd')
            const arrDays = times.map((item) => moment(item?.from).format('D.M.YYYY'));
            const nextDayIndex = arrDays.indexOf(moment(nextDay).format('D.M.YYYY'));
            if (nextDayIndex !== -1) {
                setNextDayStart(times[nextDayIndex].from)
            }
        }
    }, [times]);
   
    const handleCheckUrl = async (url: any) => {
        try {
            const response = await fetch(url);
            if (response.status == 200) {
                setStreamOptions({
                    autoplay: true,
                    controls: true,
                    responsive: true,
                    fluid: true,
                    sources: [{
                        src: url,
                        type: 'application/x-mpegURL'
                    }]
                })
                setValidUrl(true)
            } else {
                setValidUrl(false)
            }
        } catch (error) { }
    };

    return (
        <>
            <Wrapper id={`confSection${COMPONENT_TYPE.STREAM}`}>
                <Title colors={colors}>
                    {t('conference.component.stream.title')}
                    <TitleSymbol colors={colors} />
                </Title>

                {(isLoggedIn || Cookies.get(ACCESS_TOKEN_KEY_GUEST)) && !termAccessible && (
                    <>
                        {t('conference.stream.noAccess')}.
                    </>
                )}

                {(isLoggedIn || Cookies.get(ACCESS_TOKEN_KEY_GUEST)) && validUrl && (
                    <VideoJS options={streamOptions} onReady={handlePlayerReady} />
                )}

                {(isLoggedIn || Cookies.get(ACCESS_TOKEN_KEY_GUEST)) && !validUrl && isStreamingDay && (moment() <= moment(todayStart)) && (
                    <>
                        {t('conference.stream.beginToday')} {t('general.about')} {moment(todayStart).format('HH:mm')}.
                    </>
                )}

                {(isLoggedIn || Cookies.get(ACCESS_TOKEN_KEY_GUEST)) && !validUrl && isStreamingDay && (moment() > moment(todayStart)) && (moment() < moment(todayEnd)) && (
                    <>
                        {t('conference.stream.progress')}.
                    </>
                )}

                {(isLoggedIn || Cookies.get(ACCESS_TOKEN_KEY_GUEST)) && !validUrl && isStreamingDay && (moment() > moment(todayEnd)) && (
                    <>
                        {t('conference.stream.endToday')}
                        {!nextDayStart && (
                            <>.</>
                        )}
                        {nextDayStart && (
                            <>, {t('conference.stream.nextday')} {moment(nextDayStart).format('D. M. YYYY')} {t('general.about')} {moment(nextDayStart).format('H:mm')}.</>
                        )}
                    </>
                )}

                {(isLoggedIn || Cookies.get(ACCESS_TOKEN_KEY_GUEST)) && !validUrl && !isStreamingDay && moment(startConf) >= moment() && (
                    <>{t('conference.stream.begin')} {moment(startConf).format('D. M. YYYY')} {t('general.about')} {moment(startConf).format('H:mm')}.</>
                )}

                {(isLoggedIn || Cookies.get(ACCESS_TOKEN_KEY_GUEST)) && !validUrl && !isStreamingDay && moment(nextDayStart) >= moment() && nextDayStart != startConf && (
                    <>{t('conference.stream.continue')} {moment(nextDayStart).format('D. M. YYYY')} {t('general.about')} {moment(nextDayStart).format('H:mm')}.</>
                )}

                {(isLoggedIn || Cookies.get(ACCESS_TOKEN_KEY_GUEST)) && !validUrl && !isStreamingDay && moment() >= moment(endConf) && (
                    <>{t('conference.stream.end')} </>
                )}

               


            </Wrapper>

        </>
    );
};

export default ConferenceHeaderTemplate;
